import axios from 'axios';
import { api, vendorsPortalApi } from 'src/services/api';
import { dateToISOFormat } from 'src/services/utils/index';

export class OrdersService {
  static async getOrders() {
    return api.get('/order/orders');
  }

  static async getNewOrders() {
    return vendorsPortalApi.get('/api/orders', {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  static async getPendantOrders() {
    return api.get('/order/orders/A confirmar');
  }

  static async getOrderDetails(id) {
    try {
      const response = await api.get(`order/order/${id}`);
      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  static async getNewOrderDetails({ uuid }) {
    try {
      const response = await vendorsPortalApi.get(`/api/orders/${uuid}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const order = this.formatOrderItemsName({ order: response.data });

      return Promise.resolve(order);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  static formatOrderItemsName({ order }) {
    if (!order || !order.items) return order;

    const formattedItems = order.items.map(item => ({
      ...item,
      description: this.renderProductName(item.title, item.description),
    }));

    return { ...order, items: formattedItems };
  }

  static renderProductName = (title, description) => {
    if (description && description !== '' && description !== 'Default Title') {
      return `${title} | ${description}`;
    }

    return title;
  };

  static async confirmNewOrder({ uuid }) {
    return vendorsPortalApi.patch(`/api/orders/${uuid}/confirm`, null, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  static async requestReviewNewOrder({ uuid, payload }) {
    return vendorsPortalApi.patch(
      `/api/orders/${uuid}/request-review`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
  }

  static async acceptOrderStatus(id) {
    return api.put(`order/order/accept/${id}`);
  }

  static async refuseOrderStatus(id) {
    return api.put(`order/order/refuse/${id}`);
  }

  static async updateICMS(id, icmsST) {
    return api.put(`order/order/update/${id}`, {
      icms_st: icmsST || '',
    });
  }

  static async updatePedidoFile({
    orderId,
    filePDF,
    xmlFile,
    comprovante,
    transportadora,
    codigoRastreio,
    descricao,
    pedido,
    dataPostagem,
    custoEnvio,
    comprovanteEntrega,
  }) {
    const shipping_date = dataPostagem
      ? dateToISOFormat(dataPostagem.toString().concat(':00-03:00'))
      : '';

    return api.put(`order/order/update/${orderId}`, {
      danfe_fp: filePDF || '',
      nf_fp: xmlFile || '',
      cte_fp: comprovante || '',
      carrier_name: transportadora || '',
      tracking_number: codigoRastreio || '',
      observations_fp:
        descricao ||
        (pedido && pedido.Observa_es_FP ? pedido.Observa_es_FP : ''),
      shipping_date_and_time:
        !shipping_date && pedido.Data_e_hora_de_Envio
          ? dateToISOFormat(pedido.Data_e_hora_de_Envio)
          : shipping_date,
      shipping_cost:
        custoEnvio ||
        (pedido && pedido.Shipping_Cost ? pedido.Shipping_Cost : ''),
      proof_of_delivery: comprovanteEntrega.url ? comprovanteEntrega.url : '',
    });
  }

  static async updateNewOrderTracking({ uuid, trackingPayload }) {
    return vendorsPortalApi.patch(
      `/api/orders/${uuid}/tracking`,
      trackingPayload,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
  }

  static async getPreupload(body) {
    const { order_id, type, file_type } = body;
    return api.get(
      `order/order/${order_id}/upload/${type}/${file_type.toLowerCase()}`,
    );
  }

  static async postS3PresignedUrl({ uuid, documentType }) {
    return vendorsPortalApi.post(
      `/api/orders/${uuid}/documents/${documentType}`,
      null,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
  }

  static getBinaryFromFile = file =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.addEventListener('load', () => resolve(reader.result));
      reader.addEventListener('error', err => reject(err));

      reader.readAsArrayBuffer(file);
    });

  static async uploadFileToS3({ preSignedURL, file }) {
    const binary = await this.getBinaryFromFile(file);

    return axios.put(preSignedURL, binary, {
      headers: {
        'Content-Type': file.type,
      },
    });
  }

  static async saveInOrdersBackend({ uuid, documentType, documentPublicURL }) {
    return vendorsPortalApi.patch(
      `/api/orders/${uuid}/documents/${documentType}`,
      JSON.stringify({
        documentS3Url: documentPublicURL,
      }),
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
  }
}
